import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { HelpKey } from '@/help';

export type ThemePref = 'system' | 'light' | 'dark';
export type TourStatus = 'done' | 'dismissed';
export interface RecentPage { readonly path: string; readonly title: string }
/** What the mounted page's `PageHeader` publishes; overrides the route handle (pages inside nested `<Routes>` have none). */
export interface PageMeta { readonly title?: string; readonly helpKey?: HelpKey; readonly tourId?: string }

export interface AppState {
  // persisted preferences (mirrored to the server via usePreferences when signed in)
  theme: ThemePref;
  privacyBlur: boolean;
  /** Calm mode: no streaks, confetti, celebratory toasts or nudges. */
  calmMode: boolean;
  reducedMotion: boolean;
  // persisted UI state
  currentEntityId: string | null;
  /** Consolidated view across all entities (net worth & reports). */
  consolidated: boolean;
  sidebarCollapsed: boolean;
  tours: Record<string, TourStatus>;
  recentPages: RecentPage[];
  // ephemeral UI state
  helpOpen: boolean;
  helpKey: HelpKey | null;
  paletteOpen: boolean;
  shortcutsOpen: boolean;
  /** Bumped to ask TourProvider to (re)start the tour for the current route. */
  tourRequest: number;
  pageMeta: PageMeta | null;

  set: (patch: Partial<AppState>) => void;
  setEntity: (entityId: string | null) => void;
  setTour: (tourId: string, status: TourStatus | null) => void;
  pushRecent: (page: RecentPage) => void;
  openHelp: (key?: HelpKey | null) => void;
  requestTour: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      theme: 'system',
      privacyBlur: false,
      calmMode: false,
      reducedMotion: false,
      currentEntityId: null,
      consolidated: false,
      sidebarCollapsed: false,
      tours: {},
      recentPages: [],
      helpOpen: false,
      helpKey: null,
      paletteOpen: false,
      shortcutsOpen: false,
      tourRequest: 0,
      pageMeta: null,

      set: (patch) => set(patch),
      setEntity: (entityId) => set({ currentEntityId: entityId, consolidated: false }),
      setTour: (tourId, status) =>
        set((s) => {
          const tours = { ...s.tours };
          if (status) tours[tourId] = status;
          else delete tours[tourId];
          return { tours };
        }),
      pushRecent: (page) =>
        set((s) => ({ recentPages: [page, ...s.recentPages.filter((p) => p.path !== page.path)].slice(0, 6) })),
      openHelp: (key) => set((s) => ({ helpOpen: true, helpKey: key === undefined ? s.helpKey : key })),
      requestTour: () => set((s) => ({ tourRequest: s.tourRequest + 1, helpOpen: false })),
    }),
    {
      name: 'keel:ui',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        theme: s.theme,
        privacyBlur: s.privacyBlur,
        calmMode: s.calmMode,
        reducedMotion: s.reducedMotion,
        currentEntityId: s.currentEntityId,
        consolidated: s.consolidated,
        sidebarCollapsed: s.sidebarCollapsed,
        tours: s.tours,
        recentPages: s.recentPages,
      }),
    },
  ),
);

/** True when motion should be suppressed: in-app setting or OS preference. */
export const prefersReducedMotion = (): boolean =>
  useAppStore.getState().reducedMotion ||
  (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
